import { useRef, useState } from "react";
import { apiRequest } from "../../services/api";
export default function SisInvoiceReplacement({
  invoice,
  base,
  accessToken,
  allowed,
  onReplaced,
}) {
  const allocations = invoice.snapshot.allocations || [];
  const grouped =
    invoice.groupedCorrectionComplete === true ||
    (invoice.groupedCorrectionComplete !== false &&
      allocations.length > 0 &&
      allocations.every((a) => a.groupedCorrectionId));
  const [groupAmounts, setGroupAmounts] = useState(() =>
    allocations.map((a) => a.amount),
  );
  const corrected =
    allocations.length === 1 &&
    !!(invoice.remainingCorrection || allocations[0]?.remainingCorrectionId);
  const [allocationAmount, setAllocationAmount] = useState(
    allocations[0]?.amount || "",
  );
  const [open, setOpen] = useState(false),
    [values, setValues] = useState(() => ({
      invoiceNumber: invoice.invoiceNumber,
      invoiceDate: invoice.snapshot.invoiceDate,
      taxableAmount: invoice.snapshot.taxableAmount,
      cgst: invoice.snapshot.cgst,
      sgst: invoice.snapshot.sgst,
      igst: invoice.snapshot.igst,
      supplierGstin: invoice.snapshot.supplierGstin,
      evidenceReference: "",
      reviewNote: "",
      reason: "",
    })),
    [proposal, setProposal] = useState(null),
    [checks, setChecks] = useState({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [saved, setSaved] = useState(null);
  const pending = useRef(false),
    request = useRef(null);
  const body = () => ({
    ...values,
    supplierId: invoice.supplierId,
    registrationId: invoice.registrationId,
    allocations: allocations.map(({ accrualId, billNumber, amount }, i) => ({
      accrualId,
      billNumber,
      amount: grouped ? groupAmounts[i] : corrected ? allocationAmount : amount,
    })),
    date: new Date(Date.now() + 19800000).toISOString().slice(0, 10),
  });
  if (
    !grouped &&
    (invoice.groupedCorrection ||
      allocations.some((a) => a.groupedCorrectionId))
  )
    return (
      <p className="rounded border border-amber-200 p-3 text-sm text-amber-900">
        Mixed grouped-correction scope needs separate review. Replacement
        requires every original bill to have a posted grouped correction.
      </p>
    );
  if (invoice.snapshot.creditMatches?.length)
    return (
      <p className="rounded border border-indigo-200 p-3 text-sm text-indigo-900">
        Use “Full supplier invoice + separate credit notes” to replace or
        release this retained gross invoice.
        {invoice.documentaryRelease &&
          " Its documentary reconciliation is released; historical identity is retained."}
      </p>
    );
  function edit(k, v) {
    setValues((x) => ({ ...x, [k]: v }));
    setProposal(null);
    setChecks({});
    setError("");
    request.current = null;
  }
  async function preview() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    setProposal(null);
    setChecks({});
    request.current = null;
    try {
      const input = body(),
        p = await apiRequest(
          `${base}/invoices/${invoice.id}/replacement-preview`,
          { method: "POST", accessToken, body: input },
        );
      setProposal({ ...p, input });
    } catch (e) {
      setError(e.message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function execute() {
    if (
      pending.current ||
      !allowed ||
      !proposal?.executionEnabled ||
      !checks.document ||
      !checks.history ||
      !checks.books
    )
      return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      request.current ||= window.crypto.randomUUID();
      const r = await apiRequest(`${base}/invoices/${invoice.id}/replacement`, {
        method: "POST",
        accessToken,
        body: {
          ...proposal.input,
          previewHash: proposal.previewHash,
          requestKey: request.current,
          correctedInvoiceReviewed: true,
          originalRetainedConfirmed: true,
          noFinancialChangeConfirmed: true,
        },
      });
      setSaved(r);
      onReplaced?.(r);
    } catch (e) {
      setError(e.message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  if (saved || invoice.supersededBy)
    return (
      <p role="status" className="font-semibold text-amber-800">
        Superseded — historical evidence only. Replacement{" "}
        {saved?.replacementInvoiceId ||
          invoice.supersededBy.replacementInvoiceId}
        . Reload invoice history to see the current version.
      </p>
    );
  if (invoice.reversal)
    return (
      <p className="text-amber-800">
        Reconciliation reversed — historical evidence only.
      </p>
    );
  if (invoice.snapshot.acceptedVarianceId)
    return (
      <p className="text-sm text-gray-500">
        Invoice replacement is blocked for rectification-linked documents; use
        coordinated financial correction.
      </p>
    );
  if (!allowed)
    return (
      <p className="text-sm text-gray-500">
        Invoice replacement requires both accounting posting and reversal
        permissions.
      </p>
    );
  if (!allocations.length)
    return (
      <p className="text-sm text-gray-500">
        Reload invoice history to review source allocations before replacement.
      </p>
    );
  return (
    <section className="mt-2 rounded-lg border p-3 text-sm">
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
        className="rounded border px-3 py-2"
      >
        {open ? "Close replacement form" : "Replace corrected supplier invoice"}
      </button>
      {open && (
        <fieldset disabled={busy} className="mt-3 space-y-3">
          <legend className="font-semibold">
            Corrected invoice — unchanged supplier dues
          </legend>
          <p>
            {grouped
              ? `Posted grouped corrections already set financial dues. Retain every original bill/source and explicitly review each new actual allocation against current corrected availability. This does not change supplier dues. Do not net down a full invoice to fit a balance. Historical document gross ₹${invoice.snapshot.total}.`
              : corrected
                ? `The posted remaining-balance correction already sets financial dues. Keep the exact original source bill; a changed actual document total must fit its current corrected documentary balance. Do not reduce actual invoice gross to fit old return credits. Gross-invoice/credit-note offsets need separate reconciliation. Historical gross ₹${invoice.snapshot.total}.`
                : `Keep the original bills and gross total ₹${invoice.snapshot.total}.`}
            Original evidence stays immutable; only the replacement remains
            matched. No supplier payable, expense, input GST, payment or stock
            entry.
          </p>
          {corrected && (
            <label className="block">
              Corrected single-bill document allocation
              <input
                className="mt-1 w-full rounded border p-2"
                inputMode="decimal"
                value={allocationAmount}
                onChange={(e) => {
                  setAllocationAmount(e.target.value);
                  setProposal(null);
                  setChecks({});
                  setError("");
                  request.current = null;
                }}
              />
            </label>
          )}
          {grouped &&
            allocations.map((a, i) => (
              <label key={a.accrualId + ":" + a.billNumber} className="block">
                Corrected grouped allocation {a.billNumber}
                <input
                  className="mt-1 w-full rounded border p-2"
                  inputMode="decimal"
                  value={groupAmounts[i] ?? ""}
                  onChange={(e) => {
                    setGroupAmounts((old) =>
                      old.map((v, n) => (n === i ? e.target.value : v)),
                    );
                    setProposal(null);
                    setChecks({});
                    setError("");
                    request.current = null;
                  }}
                />
              </label>
            ))}
          {Object.entries({
            invoiceNumber: "Corrected invoice number",
            invoiceDate: "Corrected invoice date",
            supplierGstin: "Corrected supplier GSTIN",
            taxableAmount: "Corrected taxable amount",
            cgst: "Corrected CGST",
            sgst: "Corrected SGST",
            igst: "Corrected IGST",
            evidenceReference: "Corrected evidence reference",
            reviewNote: "Corrected document review",
            reason: "Invoice replacement reason",
          }).map(([k, label]) => (
            <label key={k} className="block">
              {label}
              <input
                type={k === "invoiceDate" ? "date" : "text"}
                className="mt-1 w-full rounded border p-2"
                value={values[k]}
                maxLength={["reason", "reviewNote"].includes(k) ? 1000 : 200}
                onChange={(e) => edit(k, e.target.value)}
              />
            </label>
          ))}
          <button
            type="button"
            disabled={
              values.reason.trim().length < 20 ||
              values.reviewNote.trim().length < 20 ||
              values.evidenceReference.trim().length < 3 ||
              busy
            }
            onClick={preview}
            className="rounded border px-3 py-2"
          >
            Preview invoice replacement
          </button>
          {error && (
            <p role="alert" className="text-red-700">
              {error}
            </p>
          )}
          {proposal && (
            <div className="space-y-2 rounded border border-amber-300 p-3">
              <p>
                Proposed version {proposal.snapshot.revision} · Invoice{" "}
                {proposal.snapshot.replacement.invoiceNumber} · Gross ₹
                {proposal.snapshot.replacement.total}
              </p>
              <p>{proposal.warning}</p>
              {[
                [
                  "document",
                  "I reviewed the corrected supplier document and tax components.",
                ],
                [
                  "history",
                  "The original remains historical; only this replacement is reconciled.",
                ],
                [
                  "books",
                  "Supplier dues are unchanged; no input GST, payment or new financial entry.",
                ],
              ].map(([k, label]) => (
                <label key={k} className="block">
                  <input
                    type="checkbox"
                    checked={!!checks[k]}
                    onChange={(e) =>
                      setChecks((x) => ({ ...x, [k]: e.target.checked }))
                    }
                  />
                  {label}
                </label>
              ))}
              <button
                type="button"
                disabled={
                  busy ||
                  !proposal.executionEnabled ||
                  !checks.document ||
                  !checks.history ||
                  !checks.books
                }
                onClick={execute}
                className="rounded bg-primary-700 px-3 py-2 text-white disabled:opacity-40"
              >
                Retain replacement and supersede original
              </button>
            </div>
          )}
        </fieldset>
      )}
    </section>
  );
}
